/**
 * The brand catalogue on `GET /v1/brands` — the pure parts of `/brands`.
 *
 * THE LIST IS THE VOCABULARY. Letters, chips and their labels come from the response
 * (`letters`, `categories`), never from a front-end constant: a letter the API counts at 0 is
 * grey, a chip the API counts at 0 is grey, and the eight chip words arrive in the page's
 * language. The search and the chip filter are client-side over the ≤ 5 k rows in hand.
 */

import type { LocationQuery } from 'vue-router'
import type {
  BrandChipCode,
  BrandLetterGroup,
  BrandsListResponse,
  BrandSummary,
  PopularBrandsResponse,
} from '@/features/brands/api/brandsV1'

export type FairBrandsV2Sort = 'asc' | 'desc'
export type FairBrandsV2AlphabetId = 'latin' | 'cyrillic' | 'digits'

/** The API's group names → the page's section ids (the i18n keys of the section titles). */
const ALPHABET_OF: Record<BrandLetterGroup, FairBrandsV2AlphabetId> = {
  latin: 'latin',
  cyrillic: 'cyrillic',
  digit: 'digits',
}
const GROUP_ORDER: BrandLetterGroup[] = ['latin', 'cyrillic', 'digit']

export interface FairBrandsV2LetterGroup {
  letter: string
  brands: BrandSummary[]
}

export interface FairBrandsV2AlphabetSection {
  id: FairBrandsV2AlphabetId
  titleKey: FairBrandsV2AlphabetId
  /** The full strip, in the API's order (A–Z / А–Я / 0–9). */
  indexLetters: string[]
  /** Letters with at least one brand AFTER the filters — the others render grey. */
  available: Set<string>
  groups: FairBrandsV2LetterGroup[]
}

export function normalizeFairBrandsV2Letter(letter: string | null | undefined): string {
  if (!letter) return ''
  const value = String(letter).trim()
  if (!value) return ''
  if (/\d/.test(value.charAt(0))) return value.charAt(0)
  return value.charAt(0).toLocaleUpperCase('ru-RU')
}

export function fairBrandsV2LetterSectionId(letter: string): string {
  return `fair-brands-letter-${encodeURIComponent(normalizeFairBrandsV2Letter(letter))}`
}

export function fairBrandsV2AlphabetSectionId(id: FairBrandsV2AlphabetId): string {
  return `fair-brands-alphabet-${id}`
}

export function parseFairBrandsV2LetterQuery(value: LocationQuery[string] | undefined): string {
  const raw = Array.isArray(value) ? value[0] : value
  return normalizeFairBrandsV2Letter(typeof raw === 'string' ? raw : '')
}

/** Case- and accent-insensitive containment over the canonical name. */
export function filterFairBrandsV2(
  brands: readonly BrandSummary[],
  options: { search?: string; categories?: readonly BrandChipCode[] },
): BrandSummary[] {
  const search = (options.search ?? '').trim().toLocaleLowerCase('ru-RU')
  const categories = options.categories ?? []
  return brands.filter((brand) => {
    if (search && !brand.name_canonical.includes(search) && !brand.name.toLocaleLowerCase('ru-RU').includes(search)) {
      return false
    }
    if (categories.length > 0 && !categories.some((code) => brand.categories?.includes(code))) return false
    return true
  })
}

/**
 * The sections of the directory: one per letter group the API serves, the strip from the
 * API's `letters` (every letter, in order), the groups from the filtered brands. A group with
 * no brand at all after the filters is dropped — the page hides an empty alphabet.
 */
export function buildFairBrandsV2Sections(
  list: BrandsListResponse,
  options: { search?: string; categories?: readonly BrandChipCode[]; sort?: FairBrandsV2Sort },
): FairBrandsV2AlphabetSection[] {
  const sort = options.sort ?? 'asc'
  const filtered = filterFairBrandsV2(list.brands, options)
  const byLetter = new Map<string, BrandSummary[]>()
  for (const brand of filtered) {
    const key = `${brand.letter_group}:${brand.initial}`
    const held = byLetter.get(key) ?? []
    held.push(brand)
    byLetter.set(key, held)
  }
  const sections: FairBrandsV2AlphabetSection[] = []
  for (const group of GROUP_ORDER) {
    const strip = Object.keys(list.letters?.[group] ?? {})
    if (strip.length === 0) continue
    const order = sort === 'desc' ? [...strip].reverse() : strip
    const groups: FairBrandsV2LetterGroup[] = []
    const available = new Set<string>()
    for (const letter of order) {
      const rows = byLetter.get(`${group}:${letter}`)
      if (!rows?.length) continue
      const sorted = [...rows].sort((a, b) => a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' }))
      if (sort === 'desc') sorted.reverse()
      groups.push({ letter, brands: sorted })
      available.add(letter)
    }
    if (groups.length === 0) continue
    sections.push({ id: ALPHABET_OF[group], titleKey: ALPHABET_OF[group], indexLetters: order, available, groups })
  }
  return sections
}

export interface FairBrandsV2Tile {
  slug: string
  name: string
  coverUrl: string | null
  logoUrl: string | null
}

export function mapFairBrandsV2PopularTiles(popular: PopularBrandsResponse | null | undefined): FairBrandsV2Tile[] {
  return (popular?.brands ?? []).map((brand) => ({
    slug: brand.slug,
    name: brand.name,
    coverUrl: brand.cover_url ?? null,
    logoUrl: brand.logo_url ?? null,
  }))
}

/** The caption under «Popular brands»: the i18n key for WHY these houses, by the API's reason. */
export function fairBrandsV2PopularCaptionKey(popular: PopularBrandsResponse | null | undefined): string {
  if (!popular) return 'fair_brands.popular_caption.trending'
  if (popular.personalized && popular.reason === 'viewed') return 'fair_brands.popular_caption.viewed'
  if (popular.personalized && popular.reason === 'declared') return 'fair_brands.popular_caption.declared'
  return 'fair_brands.popular_caption.trending'
}

/** Route param → the slug the API takes (an old link may still carry a NAME — the page resolves it). */
export function resolveFairBrandsV2Param(param: string | string[] | undefined): string {
  const raw = Array.isArray(param) ? param[0] : param
  if (!raw) return ''
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

/** A slug is lower-case ASCII with dashes; anything else is a name to resolve through the typeahead. */
export function looksLikeFairBrandsV2Slug(value: string): boolean {
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)
}
